import { useEffect, useState } from "react";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { Plus, X, Trash2, Bell, Check } from "lucide-react";
import { toast } from "sonner";

const SEVERITY_COLORS = {
  critical: "pill-red",
  warning: "pill-yellow",
  info: "pill-blue",
};

const PRESETS = [
  { title: "Actualizar banners web", category: "banner_web", severity: "warning" },
  { title: "Actualizar banners app", category: "banner_app", severity: "warning" },
  { title: "Revisar portada de Facebook", category: "facebook_cover", severity: "info" },
  { title: "Revisar historias destacadas IG", category: "instagram_highlights", severity: "info" },
  { title: "Actualizar promoción vigente en web", category: "promocion_web", severity: "warning" },
  { title: "Actualizar promoción vigente en app", category: "promocion_app", severity: "warning" },
  { title: "Revisar links de WhatsApp", category: "whatsapp", severity: "info" },
  { title: "Revisar campañas por vencer", category: "campana_vencer", severity: "critical" },
  { title: "Revisar sucursales sin contenido reciente", category: "sucursal_sin_contenido", severity: "warning" },
  { title: "Revisar creativos de pauta activos", category: "pauta", severity: "info" },
  { title: "Preparar materiales para inauguración", category: "inauguracion", severity: "info" },
  { title: "Preparar materiales para aniversario", category: "aniversario", severity: "info" },
];

const Alerts = () => {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState("activa");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/alerts").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = filter === "all" ? items : items.filter((a) => a.status === filter);

  const markResolved = async (id) => {
    await api.patch(`/alerts/${id}`, { status: "atendida", resolved_at: new Date().toISOString() });
    toast.success("Atendido");
    load();
  };

  return (
    <Layout
      title="Avisos Estratégicos"
      subtitle="Recordatorios alternos y tareas generales"
      actions={
        <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2" data-testid="new-alert-btn">
          <Plus size={14} /> Nuevo aviso
        </button>
      }
    >
      <div className="flex gap-2 mb-5">
        {["activa", "atendida", "all"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} className={`pill cursor-pointer ${filter === f ? "pill-yellow" : "pill-gray"}`} data-testid={`alert-filter-${f}`}>
            {f === "activa" ? "Activas" : f === "atendida" ? "Atendidas" : "Todas"}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><span className="spinner" /></div>
      ) : filtered.length === 0 ? (
        <div className="aura-card-flat p-12 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#D99D26]/15 text-[#8a5e10] flex items-center justify-center mx-auto mb-4">
            <Bell size={24} />
          </div>
          <h3 className="font-display text-2xl font-medium">Sin avisos</h3>
          <p className="text-[#666] mt-2 mb-5">Crea recordatorios para banners, promociones, sucursales, etc.</p>
          <button onClick={() => setShowForm(true)} className="btn-primary">Crear aviso</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map((a) => (
            <div key={a.id} className="aura-card p-4 flex items-start gap-3" data-testid={`alert-${a.id}`}>
              <div className={`w-2 h-2 rounded-full mt-2 ${a.severity === "critical" ? "bg-red-500" : a.severity === "warning" ? "bg-[#D99D26]" : "bg-blue-500"}`} />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm">{a.title}</div>
                {a.description && <div className="text-xs text-[#666] mt-0.5">{a.description}</div>}
                <div className="flex gap-2 mt-2">
                  <span className={`pill ${SEVERITY_COLORS[a.severity] || "pill-gray"} text-[10px]`}>{a.severity}</span>
                  <span className="pill pill-gray text-[10px]">{a.category}</span>
                </div>
              </div>
              <div className="flex gap-1">
                {a.status === "activa" && (
                  <button onClick={() => markResolved(a.id)} className="text-[#666] hover:text-green-600" title="Marcar atendido"><Check size={14} /></button>
                )}
                <button onClick={async () => {
                  if (!window.confirm("¿Eliminar?")) return;
                  await api.delete(`/alerts/${a.id}`);
                  load();
                }} className="text-[#666] hover:text-red-600"><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && <AlertForm onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </Layout>
  );
};

const AlertForm = ({ onClose, onSaved }) => {
  const [form, setForm] = useState({ title: "", description: "", category: "general", severity: "info", status: "activa" });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    await api.post("/alerts", form);
    toast.success("Aviso creado");
    onSaved();
  };

  const applyPreset = (p) => setForm({ ...form, title: p.title, category: p.category, severity: p.severity });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl w-full max-w-lg p-7 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start"><div><div className="label-overline">Crear</div><h2 className="font-display text-2xl font-medium">Nuevo aviso</h2></div><button type="button" onClick={onClose}><X size={18} /></button></div>
        <div>
          <div className="label-overline mb-2">Plantillas rápidas</div>
          <div className="flex flex-wrap gap-1.5">
            {PRESETS.map((p, i) => (
              <button key={i} type="button" onClick={() => applyPreset(p)} className="pill pill-gray cursor-pointer text-[10px]">{p.title}</button>
            ))}
          </div>
        </div>
        <div><label className="aura-label">Título *</label><input required className="aura-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="alert-title" /></div>
        <div><label className="aura-label">Descripción</label><input className="aura-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="aura-label">Severidad</label>
            <select className="aura-input" value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })}>
              <option value="info">Info</option>
              <option value="warning">Atención</option>
              <option value="critical">Crítico</option>
            </select>
          </div>
          <div>
            <label className="aura-label">Categoría</label>
            <input className="aura-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
          </div>
        </div>
        <button type="submit" className="btn-primary w-full justify-center" data-testid="alert-submit">Crear aviso</button>
      </form>
    </div>
  );
};

export default Alerts;
